import {
    IPEDS_WIZARD_FETCH_SELECT_ITEMS_REQUEST,
    IPEDS_WIZARD_FETCH_SELECT_ITEMS_SUCCESS,
    IPEDS_WIZARD_FETCH_SELECT_ITEMS_FAILURE,
    IPEDS_WIZARD_SET_ACTIVE_STEP,
    IPEDS_WIZARD_SET_EXTRACT,
    IPEDS_WIZARD_SET_PARAMETER,
    IPEDS_WIZARD_GENERATE_REPORT_REQUEST,
    IPEDS_WIZARD_GENERATE_REPORT_SUCCESS,
    IPEDS_WIZARD_GENERATE_REPORT_FAILURE,
    IPEDS_WIZARD_RESET_PARAMETERS,
    IPEDS_WIZARD_RESET
} from './ipedsWizardTypes';
import { AWARDS_EXTRACT_CODE, DEFAULT_OUTPUT_FORMAT, E12_EXTRACT_CODE, EF_EXTRACT_CODE, requiresTerms } from '../data/ipedsExtractMeta';
import { awardsInitialParameters } from '../data/awardsParameters';
import { E12_INITIAL_PARAMETERS } from '../data/e12Parameters';
import { EF_INITIAL_PARAMETERS, defaultFallReportingDate } from '../data/efParameters';
import { reportingPeriodFor } from '../data/reportingScope';

export const initialParameters = {
    reportingYearId: '',
    reportType: '',
    termIds: [],
    awardStatusIds: [],
    outputFormat: DEFAULT_OUTPUT_FORMAT
};

/**
 * Each extract starts from its own defaults. SFA preselects PDF, its only enabled format;
 * Awards & Completions and 12-Month Enrollment have two equally valid formats, so they start
 * with neither and ask.
 */
export const initialParametersFor = (extract, selectItems) => {
    if (extract === AWARDS_EXTRACT_CODE) return awardsInitialParameters(selectItems);
    if (extract === E12_EXTRACT_CODE) return { ...E12_INITIAL_PARAMETERS };
    if (extract === EF_EXTRACT_CODE) return { ...EF_INITIAL_PARAMETERS };
    return { ...initialParameters };
};

/**
 * The shape the EF extract must return for Review Students:
 * - eligible: one row per reported student, with `warning` ("W07 Citizenship not recorded") or ''.
 * - warnings: one row per warning: studentID, lastName, firstName, name, code, warning, field, value.
 * - errors: one row per error for an excluded student: the same identity fields, code, reason,
 *   field, value, and `isCohort` for a first-time, degree-seeking undergraduate.
 */
export const EMPTY_EF_REVIEW = { eligible: [], warnings: [], errors: [] };

export const initialState = {
    isLoading: false,
    activeStep: 1,
    furthestStep: 1,
    extract: '',
    selectItems: {
        institutionName: '',
        academicYear: [],
        reportType: [],
        terms: [],
        awardStatus: [],
        cipCodeYears: [],
        ipedsAwardLevels: [],
        fallEnrollmentSetup: null
    },
    parameters: { ...initialParameters },
    // FTE Instructional Staff by collection year. It belongs to the year, not to a run, so it
    // outlives Cancel and Start Over and comes back when that year is selected again.
    efFteByCollectionYear: {},
    // Fall Enrollment snapshots by collection year: { status, studentCount, savedOn, savedBy,
    // unlockedOn }. Nothing loads them yet, so every year reads as having no snapshot.
    efSnapshots: {},
    // Fall Enrollment's Review Students rows: { eligible, warnings, errors }. Nothing loads them
    // until the EF extract is built, so Step 3 shows empty grids and zero counts.
    efReview: EMPTY_EF_REVIEW,
    report: null,
    isGenerating: false,
    generateFailed: false
};

// The blank SelectItem the API prepends to Academic Year is a <select> placeholder; a
// type-ahead has its own empty state, so an option with no label would just be an
// unlabelled row the user can pick.
const withoutBlanks = (items) => (items || []).filter((item) => item && item.displayText);

const ipedsWizardReducer = (state = initialState, action = {}) => {
    switch (action.type) {
        case IPEDS_WIZARD_FETCH_SELECT_ITEMS_REQUEST:
            return { ...state, isLoading: true };

        case IPEDS_WIZARD_FETCH_SELECT_ITEMS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                selectItems: {
                    institutionName: action.payload?.institutionName || '',
                    academicYear: withoutBlanks(action.payload?.academicYear),
                    reportType: withoutBlanks(action.payload?.reportType),
                    terms: withoutBlanks(action.payload?.terms),
                    awardStatus: withoutBlanks(action.payload?.awardStatus),
                    cipCodeYears: withoutBlanks(action.payload?.cipCodeYears),
                    ipedsAwardLevels: withoutBlanks(action.payload?.ipedsAwardLevels),
                    fallEnrollmentSetup: action.payload?.fallEnrollmentSetup || null
                }
            };

        case IPEDS_WIZARD_FETCH_SELECT_ITEMS_FAILURE:
            return { ...state, isLoading: false };

        case IPEDS_WIZARD_SET_ACTIVE_STEP:
            return {
                ...state,
                activeStep: action.payload,
                furthestStep: Math.max(state.furthestStep, action.payload)
            };

        // Parameters belong to the extract they were entered for: carrying SFA's PDF format or
        // award statuses into Awards & Completions would pre-fill fields that mean nothing there.
        case IPEDS_WIZARD_SET_EXTRACT:
            if (action.payload === state.extract) return state;
            return {
                ...state,
                extract: action.payload,
                parameters: initialParametersFor(action.payload, state.selectItems),
                report: null,
                generateFailed: false
            };

        case IPEDS_WIZARD_SET_PARAMETER: {
            const { field, value } = action.payload;
            let parameters = { ...state.parameters, [field]: value };
            // Picking a reporting year resets the period to that year's July 1 – June 30; the
            // dates are only edited by hand when an institution's period differs.
            if (field === 'reportingYear' && value) {
                parameters = { ...parameters, ...reportingPeriodFor(value) };
            }
            // Terms only scope the Academic Reporter population. Leaving a stale selection
            // behind would silently narrow a Program or Military/Veteran run.
            if (field === 'reportType' && !requiresTerms(value)) {
                parameters.termIds = [];
            }
            let { efFteByCollectionYear } = state;
            if (state.extract === EF_EXTRACT_CODE) {
                // A collection year brings its October 15 reporting date and its own saved FTE;
                // a year with nothing saved starts empty.
                if (field === 'collectionYear') {
                    parameters.fallReportingDate = defaultFallReportingDate(value);
                    parameters.fteInstructionalStaff = (value && efFteByCollectionYear[value]) || '';
                }
                if (field === 'fteInstructionalStaff' && parameters.collectionYear) {
                    efFteByCollectionYear = { ...efFteByCollectionYear, [parameters.collectionYear]: value };
                }
            }
            // Any parameter change invalidates a report generated from the previous ones.
            return { ...state, parameters, efFteByCollectionYear, report: null, generateFailed: false };
        }

        case IPEDS_WIZARD_GENERATE_REPORT_REQUEST:
            return { ...state, isGenerating: true, generateFailed: false };

        case IPEDS_WIZARD_GENERATE_REPORT_SUCCESS:
            return { ...state, isGenerating: false, generateFailed: false, report: action.payload };

        case IPEDS_WIZARD_GENERATE_REPORT_FAILURE:
            return { ...state, isGenerating: false, generateFailed: true, report: null };

        // Cancel: the acceptance criteria ask for the fields to reset, not for the wizard to
        // close, so the loaded select-items and the chosen extract survive.
        case IPEDS_WIZARD_RESET_PARAMETERS:
            return {
                ...state,
                parameters: initialParametersFor(state.extract, state.selectItems),
                report: null,
                isGenerating: false,
                generateFailed: false,
                activeStep: 1,
                furthestStep: 1
            };

        case IPEDS_WIZARD_RESET:
            return {
                ...initialState,
                selectItems: state.selectItems,
                efFteByCollectionYear: state.efFteByCollectionYear,
                efSnapshots: state.efSnapshots
            };

        default:
            return state;
    }
};

export default ipedsWizardReducer;
